import Swiper from "swiper";
import { Navigation, EffectFade, Pagination, A11y } from "swiper/modules";
import { VideoPlayer } from "./player";
import { animate, inView } from "motion";
import { appendFormData, updateURLSearchParams } from "./helpers";

document.addEventListener("DOMContentLoaded", function () {
    const postGrid = document.querySelector(".post-grid");
    if (!postGrid) return;

    const init = () => {
        inView(
            postGrid,
            (element) => {
                element.querySelectorAll(".card")?.forEach((card, index) => {
                    const offsetX = 30 + index * 10;
                    const offsetY = 30 + index * 50;

                    animate(
                        card,
                        {
                            opacity: [0, 1],
                            x: [-offsetX, 0],
                            y: [offsetY, 0],
                        },
                        {
                            duration: 0.8,
                            easing: "ease-out",
                        }
                    );
                });
            },
            {
                once: true,
                margin: "0px 0px -550px 0px",
            }
        );

        const dialog = postGrid.querySelector(".post-grid__dialog");
        const cards = postGrid.querySelectorAll(
            ".post-grid__results .card:has(button.card__button)"
        );
        if (!dialog || !cards) return;

        cards.forEach((card, index) => {
            const button = card.querySelector("button.card__button");
            button.addEventListener("click", () => {
                dialog.querySelector(".swiper-wrapper").innerHTML = [...cards].reduce(
                    (slides, card) =>
                        (slides += `<div class="swiper-slide">${card.outerHTML}</div>`),
                    ""
                );
                dialog.showModal();
                const swiper = new Swiper(dialog.querySelector(".swiper"), {
                    initialSlide: index,
                    slidesPerView: 1,
                    effect: "fade",
                    fadeEffect: {
                        crossFade: true,
                    },
                    navigation: {
                        nextEl: ".swiper-button-next",
                        prevEl: ".swiper-button-prev",
                    },
                    pagination: {
                        el: ".swiper-pagination",
                        type: "fraction",
                    },
                    modules: [Navigation, EffectFade, Pagination, A11y],
                });

                const mouseEventHandlers = [];
                const hideTimeouts = [];

                dialog.querySelectorAll(".card")?.forEach((card, cardIndex) => {
                    const slide = card.parentElement;
                    const player = new VideoPlayer(card.querySelector(".card__video .player"), {
                        controls: { position: "left" },
                    });
                    const wrapper = card.querySelector(".card__wrapper");
                    const hasVideo = card.querySelector(".card__video");

                    let isWrapperVisible = false;

                    animate(wrapper, {
                        x: "-100%",
                        opacity: 0,
                    });

                    const showWrapper = () => {
                        if (!isWrapperVisible) {
                            isWrapperVisible = true;
                            animate(
                                wrapper,
                                {
                                    x: ["-100%", "0%"],
                                    opacity: 1,
                                },
                                {
                                    duration: 0.6,
                                    easing: "ease-out",
                                }
                            );
                        }
                    };

                    const hideWrapper = () => {
                        if (isWrapperVisible) {
                            isWrapperVisible = false;
                            animate(
                                wrapper,
                                {
                                    x: "0%",
                                    opacity: 0,
                                },
                                {
                                    duration: 0.4,
                                    easing: "ease-out",
                                }
                            );
                        }
                    };

                    const resetHideTimer = () => {
                        clearTimeout(hideTimeouts[cardIndex]);
                        if (hasVideo && isWrapperVisible) {
                            hideTimeouts[cardIndex] = setTimeout(hideWrapper, 5000);
                        }
                    };

                    const handleMouseMove = () => {
                        const isActive = slide.className.includes("swiper-slide-active");
                        if (isActive && hasVideo) {
                            showWrapper();
                            resetHideTimer();
                        }
                    };

                    mouseEventHandlers[cardIndex] = handleMouseMove;

                    const handleActiveChange = () => {
                        const isActive = slide.className.includes("swiper-slide-active");

                        if (isActive) {
                            if (player?.container) {
                                player?.play();
                            }

                            if (hasVideo) {
                                showWrapper();
                                resetHideTimer();
                                dialog.addEventListener("mousemove", handleMouseMove);
                            } else {
                                showWrapper();
                            }
                        } else {
                            if (player?.container) {
                                player?.pause();
                            }

                            clearTimeout(hideTimeouts[cardIndex]);
                            dialog.removeEventListener("mousemove", handleMouseMove);

                            if (!hasVideo) {
                                hideWrapper();
                            }
                        }
                    };

                    const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                            if (
                                mutation.type === "attributes" &&
                                mutation.attributeName === "class"
                            ) {
                                handleActiveChange();
                            }
                        });
                    });

                    observer.observe(slide, { attributes: true });

                    handleActiveChange();
                });

                const handleDialogClose = () => {
                    dialog.removeEventListener("close", handleDialogClose);

                    // Clean up all mouse event handlers and timeouts
                    mouseEventHandlers.forEach((handler) => {
                        dialog.removeEventListener("mousemove", handler);
                    });
                    hideTimeouts.forEach((timeout) => {
                        clearTimeout(timeout);
                    });

                    swiper.destroy();
                };

                dialog.addEventListener("close", handleDialogClose);
            });
        });
    };

    const filters = postGrid?.querySelector(".filters");
    if (filters) {
        const getPostGrid = async () => {
            const results = postGrid.querySelector(".post-grid__results");
            const args = JSON.parse(results.getAttribute("data-args"));
            const formData = new FormData();
            formData.append("action", "get_post_grid");

            appendFormData(formData, args);
            new URLSearchParams(window.location.search).forEach((value, key) => {
                formData.append(key, value);
            });

            try {
                const response = await fetch(dna.ajax_url, {
                    method: "POST",
                    body: formData,
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const resData = await response.json();
                if (response.ok) {
                    results.innerHTML = resData.data.results;
                }
            } catch (error) {
                console.error("Error:", error);
            }
        };

        filters.addEventListener("submit", async (e) => {
            e.preventDefault();
            updateURLSearchParams(e.target);
            await getPostGrid();

            init();
        });
    }

    init();
});
